import { useEffect, useState } from 'react';
import { getAppVersionAdoption } from '../services/cityOperationService';

/**
 * Distinct app versions + the live location_ping gate, for the "App version" list filter.
 *
 * Sourced from /admin/ops/app-adoption, which already returns the version histogram — no second
 * endpoint needed. Three deliberate behaviours:
 *
 *  - DEGRADES GRACEFULLY. That endpoint is `ops:bulk`-gated, but the driver/user lists are visible to
 *    staff without that permission. A 403 must not break their filter bar, so a failure leaves
 *    `versions` empty and the dropdown still offers All / Old app / Probe-eligible — the buckets that
 *    matter most and need no server data to describe.
 *  - CACHED PER SESSION, not per mount. Behind it sits a groupBy + 3 COUNTs over `users`; on a large
 *    fleet that is the heaviest query on the biggest table. Without this memo every navigation between
 *    the driver and user lists would re-request it. The version list changes when an app ships, not
 *    when someone clicks a tab, so a page-lifetime cache is the right granularity. (The server also
 *    caches for an hour — this just stops the needless round-trip.)
 *  - DEDUPES IN FLIGHT. Both lists mounting together share one request instead of racing.
 */
export interface AppVersionOptions {
  versions: number[];   // distinct known versions, newest first (NULL bucket excluded)
  gate: number;         // LOCATION_PING_MIN_APP_VERSION; 0 = gate off
  loaded: boolean;      // false while in flight
  available: boolean;   // false when the endpoint was unreachable/forbidden
}

const EMPTY: AppVersionOptions = { versions: [], gate: 0, loaded: false, available: false };

/** Resolved results and in-flight promises, keyed by role. Module scope = one per page session. */
const resolved = new Map<string, AppVersionOptions>();
const inFlight = new Map<string, Promise<AppVersionOptions>>();

const fetchOptions = (role: string): Promise<AppVersionOptions> => {
  const cached = resolved.get(role);
  if (cached) return Promise.resolve(cached);

  const pending = inFlight.get(role);
  if (pending) return pending;

  const p = getAppVersionAdoption(undefined, role as 'driver' | 'rider' | 'all')
    .then((res) => {
      const versions = (res.data.versions || [])
        .map((v) => v.version)
        .filter((v): v is number => typeof v === 'number')
        .sort((a, b) => b - a);
      const out: AppVersionOptions = {
        versions, gate: res.data.minAppVersion || 0, loaded: true, available: true,
      };
      resolved.set(role, out);
      return out;
    })
    .catch(() => {
      // Permission or network failure — keep the filter usable with the static buckets only, and
      // remember the failure so a staff member without ops:bulk does not re-request on every mount.
      const out: AppVersionOptions = { versions: [], gate: 0, loaded: true, available: false };
      resolved.set(role, out);
      return out;
    })
    .finally(() => { inFlight.delete(role); });

  inFlight.set(role, p);
  return p;
};

export const useAppVersionOptions = (role: 'driver' | 'rider' | 'all' = 'driver'): AppVersionOptions => {
  const [state, setState] = useState<AppVersionOptions>(() => resolved.get(role) ?? EMPTY);

  useEffect(() => {
    let cancelled = false;
    const cached = resolved.get(role);
    if (cached) { setState(cached); return; }
    fetchOptions(role).then((out) => { if (!cancelled) setState(out); });
    return () => { cancelled = true; };
  }, [role]);

  return state;
};
